import {
	Column,
	Conditional,
	Else,
	englishCommaJoinStrings,
	getObjectEntries,
	If,
	makeClassName,
	Row,
	Tooltip,
	TooltipContainerProps,
	TooltipProps,
	TruncatedText,
} from "@hex-insights/core";
import {
	Guardian,
	GuardianNameAndImageFragment,
	Relationship,
	Student,
	StudentNameFragment,
	useGuardianTooltipLazyQuery,
	useLazyTooltip,
} from "../../../../Utilities";
import { RelationshipFormat, StudentFormat } from "../../../Format";
import { GuardianIconNameDescription } from "../General";
import styles from "./styles.module.css";

export type TooltipGuardian = Pick<Guardian, "id"> &
	GuardianNameAndImageFragment & {
		relationships: (Pick<Relationship, "id" | "relationshipType"> & {
			student: Pick<Student, "id"> & StudentNameFragment;
		})[];
	};

export type GuardianTooltipProps = {
	isLoading?: boolean;
	guardian: TooltipGuardian | null | undefined;
} & Omit<TooltipProps, "children">;

export function GuardianTooltip({ isLoading = false, guardian, className, ...props }: GuardianTooltipProps) {
	return (
		<Tooltip className={makeClassName(styles["tooltip"], "tooltip--big", className)} {...props}>
			<Tooltip.Body>
				<Conditional>
					<If condition={isLoading && !guardian}>Loading</If>
					<Else>{!!guardian && <GuardianTooltipContent guardian={guardian} />}</Else>
				</Conditional>
			</Tooltip.Body>
		</Tooltip>
	);
}

type GuardianTooltipContentProps = {
	guardian: TooltipGuardian;
};

function GuardianTooltipContent({ guardian }: GuardianTooltipContentProps) {
	const groupedRelationships = guardian.relationships.reduce((groups, relationship) => {
		if (!groups[relationship.relationshipType]) {
			groups[relationship.relationshipType] = [];
		}
		groups[relationship.relationshipType].push(relationship);
		return groups;
	}, {} as Record<string, TooltipGuardian["relationships"]>);

	return (
		<Column justify="spaced-start">
			<GuardianIconNameDescription guardian={guardian} noLink iconSize={40} />

			<Column justify="spaced-start" verticalSpacing="0.125rem">
				{getObjectEntries(groupedRelationships).map(([relationshipType, relationships]) => (
					<Row justify="spaced-start" align="center" verticalSpacing="0.25rem">
						<span className={styles["tooltip__label"]}>
							{RelationshipFormat.Fields.relationshipType(relationshipType)} of
						</span>
						<TruncatedText className={styles["tooltip__value"]}>
							{englishCommaJoinStrings(relationships.map((r) => StudentFormat.name(r.student)))}
						</TruncatedText>
					</Row>
				))}
			</Column>
		</Column>
	);
}

export type GuardianTooltipContainerProps = Omit<TooltipContainerProps, "onOpenChange" | "contentSyncID"> & {
	guardianID: Guardian["id"];
};

GuardianTooltip.Container = function ({ guardianID, children, ...props }: GuardianTooltipContainerProps) {
	const [load, { loading, data }] = useGuardianTooltipLazyQuery({
		variables: { id: guardianID },
	});
	const { onOpenChange, contentSyncID, className: tooltipClassName } = useLazyTooltip(load, !!data);

	return (
		<Tooltip.Container onOpenChange={onOpenChange} contentSyncID={contentSyncID} {...props}>
			<GuardianTooltip isLoading={loading} guardian={data?.guardian} className={tooltipClassName} />

			{children}
		</Tooltip.Container>
	);
};
